/* eslint-disable operator-assignment*/
import React, { useEffect, useState, useRef } from 'react';
import styled from 'styled-components';
import {
  selectAll,
  select,
  event,
  scaleLinear,
  curveMonotoneX,
  symbol,
  symbolTriangle,
  axisBottom,
  axisLeft,
  line,
  mouse,
  min,
  max,
  extent,
} from 'd3';
import { transparentize } from 'polished';
//
import colors from 'theme/colors';
import { survivalPlotClassName } from 'components/visuals/survival/constants';
import { ISummaryOptions, ISummaryProduct, ISurvivalPlotItem } from 'store/deal-evaluation/types';
import uuid from 'uuid';
import messages from 'constants/messages';
import {
  survivalPlotContainer,
  survivalPlotLine,
  survivalLegend,
  survivalLegendBox,
} from 'components/visuals/survival/test-ids';
import { generateTooltipText, generateLegendText } from 'lib/helpers/visuals';
import { BubblyContainer } from 'components/visuals/_common';
import BubblyButton from 'components/buttons/bubbly';
import lineColors from 'components/visuals/multi-line-graph/colors';
import { renderNoDataGraph } from 'components/visuals/multi-line-graph/no-data-graph';
import { formatNumber } from 'lib/helpers/currency';
import font from 'theme/visuals';

const Container = styled.div`
  background-color: ${colors.white};
`;
const SURVIVAL_PLOT_USED_MIN_NUMBER = 100;
const SURVIVAL_PLOT_USED_MAX_NUMBER = 200;
const svgStrokeWidth = 2;
const legendFontWeight = 500;
const tooltipWidth = 220;
const tooltipHeight = 35;
const tooltipXValue = -110;
const tooltipYValue = -45;
const tooltipRXYValue = 4;
const tooltipTransparentizeValue = 0.3;
const tooltipD3Size = 64;
const svgStrokeWidthDecimalValue = 0.5;
const svgFontSizeValue = 12;
const tickPaddingValue = 6;
const tickArgumentsValue = 5;
const tooltipYNegativeValue = -24;
const legendStrokeWidth = 3;
const svgYConst = 8;

function renderBubblyButtons(survivalPlotItems: ISurvivalPlotItem[], setSurvivalPlotItems: Function) {
  return survivalPlotItems.map(item => (
    <BubblyButton
      key={`price-option-button-${uuid()}`}
      text={item.label}
      color={item.color}
      active={item.active}
      onClick={() =>
        setSurvivalPlotItems(
          survivalPlotItems.map(i => {
            if (i.label === item.label) {
              return {
                ...i,
                active: !i.active,
              };
            }
            return i;
          })
        )
      }
    />
  ));
}

/**
 * Survival Plot
 *
 * Displays a line graph with the survival plot
 */

interface ISurvivalPlotProps {
  productSummary: ISummaryProduct;
  pricePerUnitLabel: string;
  dealName: string;
  selectedForecastLabel: string;
  selectedCostLabel: string;
  periodStart: Date;
  periodEnd: Date;
  productItemId: string;
}

function processOptions(options: ISummaryOptions, survivalPlotItemsNew: ISurvivalPlotItem[]) {
  let lineColorIndex = 0;
  // Cap
  if (options.cap) {
    survivalPlotItemsNew.push({
      label: 'Cap',
      color: lineColors[lineColorIndex],
      active: true,
      optionSummary: options.cap,
    });
    lineColorIndex = lineColorIndex + 1;
  }
  // Floor
  if (options.floor) {
    survivalPlotItemsNew.push({
      label: 'Floor',
      color: lineColors[lineColorIndex],
      active: true,
      optionSummary: options.floor,
    });
    lineColorIndex = lineColorIndex + 1;
  }
  // Push
  if (options.push) {
    survivalPlotItemsNew.push({
      label: 'Push',
      color: lineColors[lineColorIndex],
      active: true,
      optionSummary: options.push,
    });
    lineColorIndex = lineColorIndex + 1;
  }
  // Pull
  if (options.pull) {
    survivalPlotItemsNew.push({
      label: 'Pull',
      color: lineColors[lineColorIndex],
      active: true,
      optionSummary: options.pull,
    });
    lineColorIndex = lineColorIndex + 1;
  }
  // Early Term
  if (options.earlyTerm) {
    survivalPlotItemsNew.push({
      label: 'Early Term',
      color: lineColors[lineColorIndex],
      active: true,
      optionSummary: options.earlyTerm,
    });
    lineColorIndex = lineColorIndex + 1;
  }
  // Extension
  if (options.extension) {
    survivalPlotItemsNew.push({
      label: 'Extension',
      color: lineColors[lineColorIndex],
      active: true,
      optionSummary: options.extension,
    });
    lineColorIndex = lineColorIndex + 1;
  }
  // Customer flex
  if (options.flex) {
    survivalPlotItemsNew.push({
      label: 'Customer Flex',
      color: lineColors[lineColorIndex],
      active: true,
      optionSummary: options.flex,
    });
    lineColorIndex = lineColorIndex + 1;
  }
  // Price switch
  if (options.priceSwitch) {
    survivalPlotItemsNew.push({
      label: 'Price Switch',
      color: lineColors[lineColorIndex],
      active: true,
      optionSummary: options.priceSwitch,
    });
  }
}

function minXValidate(minX: null | number, survivalMin: number) {
  return minX === null || survivalMin < minX;
}

function maxXValidate(maxX: null | number, survivalMax: number) {
  return maxX === null || survivalMax > maxX;
}

const getItemOptionSummaryAve = (item: ISurvivalPlotItem) => {
  return Math.abs(item.optionSummary.ave || 0) < SURVIVAL_PLOT_USED_MIN_NUMBER ? 1 : 0;
};

const getSurvivalPlotDimensions = (lineGraphRef: React.RefObject<HTMLDivElement>, aspectRatio: number) => {
  const dimensions = { width: 900, height: 600 };
  // Calculate dimensions
  if (lineGraphRef?.current) {
    dimensions.width = lineGraphRef.current.offsetWidth;
    dimensions.height = lineGraphRef.current.offsetWidth * aspectRatio;
  }

  // Ensure that height does not bleed off screen
  const windowHeight = window.innerHeight;
  const maxWindowHeight = windowHeight - SURVIVAL_PLOT_USED_MAX_NUMBER; // 132 = sum of menu heights
  if (dimensions.height > maxWindowHeight) {
    dimensions.height = maxWindowHeight;
  }
  return dimensions;
};

const checkActiveSurvivalPlotItemsLength = (activeSurvivalPlotItems: ISurvivalPlotItem[]) => {
  return !activeSurvivalPlotItems.length;
};

export default ({
  productSummary,
  pricePerUnitLabel,
  dealName,
  selectedForecastLabel,
  selectedCostLabel,
  periodStart,
  periodEnd,
  productItemId,
}: ISurvivalPlotProps) => {
  const aspectRatio = 0.667;
  const toolTipConstValue = 100;
  const fontFamily = 'font-family';
  const dataTestId = 'data-testid';
  const strokeWidth = 'stroke-width';
  const fontWeight = 'font-weight';
  const lineGraphRef = useRef<HTMLDivElement>(null);
  const [survivalPlotItems, setSurvivalPlotItems] = useState<ISurvivalPlotItem[]>([]);
  const [activeSurvivalPlotItems, setActiveSurvivalPlotItems] = useState<ISurvivalPlotItem[]>([]);

  // generate unique class name for svg container
  const uniqueSurvivalPlotClassName = `${survivalPlotClassName}-${productItemId}`;

  // Filter out inactive items
  useEffect(() => {
    setActiveSurvivalPlotItems(survivalPlotItems.filter(item => item.active));
  }, [survivalPlotItems]);

  if (!productSummary) {
    return <Container>{messages.warnings.noData}</Container>;
  }

  const lineGraphLegendText = generateLegendText({
    dealName,
    selectedForecastLabel,
    selectedCostLabel,
    product: productSummary.name,
    periodStart,
    periodEnd,
  });

  useEffect(() => {
    const { options } = productSummary;
    const survivalPlotItemsNew: ISurvivalPlotItem[] = [];
    processOptions(options, survivalPlotItemsNew);
    // Update state
    setSurvivalPlotItems(survivalPlotItemsNew);
  }, [productSummary]);

  useEffect(() => {
    // SVG attributes
    const dimensions = getSurvivalPlotDimensions(lineGraphRef, aspectRatio);
    const margin = {
      top: 32,
      right: 32,
      bottom: 64,
      left: 48,
    };

    const width = dimensions.width - margin.left - margin.right;
    const height = dimensions.height - margin.top - margin.bottom;

    // Set the ranges
    const x = scaleLinear().range([0, width]);
    const y = scaleLinear().range([height, 0]);

    // Calculate min and max x-value from all survival arrays
    let minX: null | number = null;
    let maxX: null | number = null;

    activeSurvivalPlotItems.forEach(item => {
      // @ts-ignore
      const survivalMin: number = min(item.optionSummary.survival);
      // @ts-ignore
      const survivalMax: number = max(item.optionSummary.survival);

      if (minXValidate(minX, survivalMin)) {
        minX = survivalMin;
      }
      if (maxXValidate(maxX, survivalMax)) {
        maxX = survivalMax;
      }
    });

    // @ts-ignore
    // Scale x and y domains
    x.domain(extent([minX, maxX]));
    y.domain([0, 1]);

    // Clear old SVG
    selectAll(`.${uniqueSurvivalPlotClassName} > *`).remove();

    // Create container
    const svg = select(lineGraphRef.current)
      .append('svg')
      .style('font-size', font.size)
      .style(fontFamily, font.family)
      .attr('width', width + margin.right + margin.left)
      .attr('height', height + margin.bottom + margin.top)
      .style('cursor', 'none')
      .append('g')
      .attr('transform', `translate(${margin.left}, ${margin.top})`)
      .attr(dataTestId, survivalPlotContainer);

    // Handle no data scenario
    if (checkActiveSurvivalPlotItemsLength(activeSurvivalPlotItems)) {
      renderNoDataGraph({ svg, x, y, width, height });
      return;
    }

    // Add data lines
    activeSurvivalPlotItems.forEach(survivalPlotItem => {
      const calculateY = (length: number, index: number): number => {
        return 1 - index / (length - 1);
      };

      // Define line
      const lineTemp: any = line()
        .x((d: any) => x(d))
        .y((_d, i: number) => y(calculateY(survivalPlotItem.optionSummary.survival.length, i)))
        .curve(curveMonotoneX);

      svg
        .append('path')
        .data([survivalPlotItem.optionSummary.survival])
        .attr('class', 'line')
        .attr('stroke', survivalPlotItem.color)
        .attr(strokeWidth, svgStrokeWidth)
        .attr('fill', 'none')
        .attr('d', lineTemp)
        .attr(dataTestId, survivalPlotLine);
    });

    // Hover tooltip
    const tooltip = svg
      .append('g')
      .attr('class', 'tooltip')
      .style('display', 'none');

    tooltip
      .append('path')
      .attr('class', 'tooltip-arrow')
      .attr(
        'd',
        symbol()
          .type(symbolTriangle)
          .size(tooltipD3Size)
      )
      .attr('fill', transparentize(tooltipTransparentizeValue, colors.darkBackgroundElevation1))
      .attr('transform', 'rotate(180) translate(0, 8)');

    tooltip
      .append('rect')
      .attr('class', 'tooltip-container')
      .style('text-align', 'center')
      .attr('fill', transparentize(tooltipTransparentizeValue, colors.darkBackgroundElevation1))
      .attr('width', tooltipWidth)
      .attr('height', tooltipHeight)
      .attr('x', tooltipXValue)
      .attr('y', tooltipYValue)
      .attr('rx', tooltipRXYValue)
      .attr('ry', tooltipRXYValue);

    tooltip
      .append('text')
      .attr('class', 'tooltip-text')
      .attr('text-anchor', 'middle')
      .attr('x', 0)
      .attr('y', tooltipYNegativeValue)
      .attr('fill', 'white');

    svg
      .append('line')
      .attr('x1', x(0))
      .attr('y1', 0)
      .attr('x2', x(0))
      .attr('y2', height)
      .style(strokeWidth, svgStrokeWidthDecimalValue)
      .style('stroke', '#000000')
      .style('stroke-dasharray', '2,3')
      .attr('class', 'x-line')
      .attr('pointer-events', 'none');

    svg
      .append('line')
      .attr('x1', 0)
      .attr('y1', y(0))
      .attr('x2', width)
      .attr('y2', y(0))
      .style(strokeWidth, svgStrokeWidthDecimalValue)
      .style('stroke', '#000000')
      .style('stroke-dasharray', '2,3')
      .attr('class', 'y-line')
      .attr('pointer-events', 'none');

    function mousemove() {
      const mouseTemp = mouse(event.target);
      const x0: number = x.invert(mouseTemp[0]); // this is the given x value on graph
      const y0: number = y.invert(mouseTemp[1]);

      // Redraw after ever mousemove event
      svg
        .select('.x-line')
        .attr('x1', x(x0))
        .attr('y1', 0)
        .attr('x2', x(x0))
        .attr('y2', height);

      svg
        .select('.y-line')
        .attr('x1', 0)
        .attr('y1', y(y0))
        .attr('x2', width)
        .attr('y2', y(y0));

      tooltip.style('display', null);
      tooltip.attr('transform', `translate(${x(x0)}, ${y(y0)})`);
      tooltip.select('.tooltip-text').text(generateTooltipText(y0, x0, pricePerUnitLabel));
      tooltip.raise(); // bring tooltip on top of guide lines
    }

    // Overlay to capture mouse events
    svg
      .append('rect')
      .attr('class', 'overlay')
      .attr('fill', 'transparent')
      .attr('width', width)
      .attr('height', height)
      .on('mouseover', () => {
        // show tooltip and cross hairs
        tooltip.style('display', null);
        svg.select('.x-line').style('stroke', '#000000');
        svg.select('.y-line').style('stroke', '#000000');
      })
      .on('mouseout', () => {
        // hide tooltip and cross hairs
        tooltip.style('display', 'none');
        svg.select('.x-line').style('stroke', 'transparent');
        svg.select('.y-line').style('stroke', 'transparent');
      })
      .on('mousemove', mousemove);

    // X axis
    svg
      .append('g')
      .attr('transform', `translate(0, ${y(0)})`)
      .style('font-size', svgFontSizeValue)
      .style(fontFamily, font.family)
      .call(
        axisBottom(x)
          .tickPadding(tickPaddingValue)
          .tickArguments([tickArgumentsValue])
          .tickFormat(d => `${typeof d === 'number' ? formatNumber(d, 0) : d.valueOf} ${pricePerUnitLabel}`)
      );

    // Y axis
    svg
      .append('g')
      .style('font-size', svgFontSizeValue)
      .style(fontFamily, font.family)
      .call(
        axisLeft(y)
          .tickPadding(tickPaddingValue)
          .tickFormat(d => `${typeof d === 'number' ? formatNumber(d, 0) : d.valueOf} ${toolTipConstValue}`)
      );

    // Legend
    svg
      .append('text')
      .attr('x', 0)
      .attr('y', height + margin.bottom - svgYConst)
      .attr(dataTestId, survivalLegend)
      .text(lineGraphLegendText);

    // Draw legend box
    const legendBoxX = width - SURVIVAL_PLOT_USED_MAX_NUMBER;
    const legendLineTextSpacing = 32;
    const legendLineTextValueSpacing = 160;
    const legendBoxYSpacing = 20;
    const legendBoxLineWidth = 16;
    let legendBoxCurrentY = 0;

    const legend = svg
      .append('g')
      .attr('transform', `translate(${legendBoxX},50)`)
      .attr(dataTestId, survivalLegendBox);

    legend
      .append('text')
      .text('Average')
      .style('font-size', font.size + svgStrokeWidth)
      .style(fontWeight, legendFontWeight);

    legendBoxCurrentY += legendLineTextSpacing;

    // Only show lines that exist in the chart
    activeSurvivalPlotItems.forEach(item => {
      legend
        .append('line')
        .style('stroke', item.color)
        .style(strokeWidth, legendStrokeWidth)
        .attr('x1', 0)
        .attr('y1', legendBoxCurrentY)
        .attr('x2', 0 + legendBoxLineWidth)
        .attr('y2', legendBoxCurrentY);
      legend
        .append('text')
        .text(item.label)
        .attr('x', legendLineTextSpacing)
        .attr('y', legendBoxCurrentY + tickArgumentsValue)
        .style(fontWeight, legendFontWeight);
      legend
        .append('text')
        .text(`${formatNumber(item.optionSummary.ave, getItemOptionSummaryAve(item))} ${pricePerUnitLabel}`)
        .attr('x', legendLineTextValueSpacing)
        .attr('y', legendBoxCurrentY + tickArgumentsValue)
        .attr('text-anchor', 'end')
        .style(fontWeight, legendFontWeight);
      legendBoxCurrentY += legendBoxYSpacing;
    });
  }, [activeSurvivalPlotItems, lineGraphLegendText, pricePerUnitLabel, uniqueSurvivalPlotClassName]);

  return (
    <Container>
      <BubblyContainer>{() => renderBubblyButtons(survivalPlotItems, setSurvivalPlotItems)}</BubblyContainer>
      <div className={uniqueSurvivalPlotClassName} ref={lineGraphRef} />
    </Container>
  );
};
